import {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useContext,
  useRef,
  useLayoutEffect,
  useState,
  type ReactNode,
} from "react";
import cx from "classnames";
import tinycolor from "tinycolor2";

import { Avatar, AvatarProps } from "../Avatar";
import { useAccessibleColor, usePrefersColorScheme } from "../../hooks";
import { ThemeProviderContext } from "../../providers";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";

import styles from "./Chip.module.scss";
import { Icon } from "../Icon";
import { LayoutUtilProps, Size } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the Chip component
 * @extends ComponentPropsWithoutRef<"span">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type ChipProps = Omit<ComponentPropsWithoutRef<"span">, "prefix"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * The text that displays in the chip.
     */
    label: string;
    /**
     * The size of the chip
     * @default medium
     */
    size?: Extract<Size, "small" | "medium">;

    /**
     * Color of the Chip
     */
    color?: string;
    /**
     * Callback for when the Chip is clicked
     *
     * @remarks
     * Adding this will make Chip focusable
     */
    onClick?: (
      e?: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>,
    ) => void;
    /**
     * Called when the component is closed. If supplied, a close button will be rendered.
     * @returns void
     */
    onClose?: (
      e: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>,
    ) => void;
    /**
     * Wraps text when it overflows
     */
    textWrap?: boolean;
    /**
     * Content displayed in the chip, before the text.
     */
    prefix?: ReactNode;

    /**
     * Image URL to display in an avatar before the chip text.
     */
    avatar?: AvatarProps["image"];
  };

/**
 * Chip component for displaying compact information or tags.
 *
 * Features:
 * - Configurable sizes (small, medium)
 * - Custom color support with automatic contrast calculation
 * - Optional click handler for interactive chips
 * - Optional close button for removable chips
 * - Text wrapping support for long labels
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation (Enter to click, Delete/Backspace to close)
 * - Theme-aware color adaptation
 * - Layout utility props for positioning and spacing
 *
 * @example
 * <Chip
 *   label="JavaScript"
 *   color="#f7df1e"
 *   onClose={(e) => console.log('Chip removed')}
 *   onClick={(e) => console.log('Chip clicked')}
 * />
 */
export const Chip = forwardRef<HTMLSpanElement, ChipProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    color,
    onClose,
    onClick,
    label,
    style,
    textWrap,
    prefix,
    avatar,
    size = "medium",
    tabIndex,
    ...rest
  } = componentProps;

  const innerRef = useRef<HTMLSpanElement>(null);
  const [lines, setLines] = useState(1);

  useLayoutEffect(() => {
    const element = innerRef.current;

    const observer = new ResizeObserver(() => {
      if (element) {
        const textNode = element.childNodes[0];
        if (textNode && textNode.textContent) {
          const range = document.createRange();
          range.setStart(textNode, 0);
          range.setEnd(textNode, textNode.textContent.length);
          setLines(range.getClientRects().length);
        }
      }
    });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  /*
   * @TODO: below to be updated with localization
   * removeTextIndex is for when removeText comes after the label depending on the language
   */
  const removeText = ", removeable";
  const newLabel = `${label}${onClose ? removeText : ""}`;

  const ChipClassNames = cx(styles["chip"], className, {
    [styles["size-small"]]: size === "small",
    [styles["has-close"]]: !!onClose,
    [styles["text-wrap"]]: textWrap,
  });

  const AvatarClassNames = cx(styles["avatar"], {
    [styles["multiline"]]: textWrap && lines > 1,
  });

  const { mode: themeMode } = useContext(ThemeProviderContext);
  const { mode: sysMode } = usePrefersColorScheme();
  const mode = themeMode ?? sysMode;

  const { foreground, background, border } = useAccessibleColor(color, mode);

  const bgObj = tinycolor(background);

  const styleWithColor = {
    "--int-bg-color": color && background,
    "--int-font-color": color && foreground,
    "--int-border-color": color && border,
    "--int-close-background-color": color
      ? bgObj.isDark()
        ? bgObj.lighten(15).toHexString()
        : bgObj.darken(15).toHexString()
      : "#14141429",
    ...style,
    ...layoutStyles,
  };

  const onKeyUpHandler = (e: KeyboardEvent<HTMLSpanElement>) => {
    switch (e.code) {
      case "Delete":
      case "Backspace":
        onClose?.(e);
        break;

      case "Enter":
        onClick?.(e);
        break;

      default:
        break;
    }
  };

  return (
    <span
      className={ChipClassNames}
      style={styleWithColor}
      data-anv="chip"
      role={onClick ? "button" : undefined}
      {...(onClick || onClose ? { ["aria-label"]: newLabel } : {})}
      {...rest}
      ref={ref}
      onClick={onClick}
      onKeyUp={onKeyUpHandler}
      tabIndex={onClick || onClose ? 0 : tabIndex}
    >
      {avatar != null ? (
        <span className={AvatarClassNames}>
          <Avatar
            className={styles.avatarInner}
            name={label}
            color={color}
            image={avatar}
            size={size === "small" ? "xsmall" : "small"}
          />
        </span>
      ) : null}

      {prefix != null ? <span className={styles.prefix}>{prefix}</span> : null}

      <span className={styles.inner} ref={innerRef}>
        {label}
      </span>

      {!!onClose && (
        // this is disabled because this is fake button that only mouse users will interact
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <span
          aria-hidden
          aria-label="remove button"
          className={styles["close"]}
          onClick={(e) => {
            e.stopPropagation();
            onClose(e);
          }}
        >
          <Icon aria-hidden svg={Close} size={size} />
        </span>
      )}
    </span>
  );
});

Chip.displayName = "Chip";
